import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AssistantProvider } from "@/context/AssistantContext";
import { AppShell } from "@/components/layout/AppShell";
import { LensMark } from "@/components/brand/ProximaLogo";
import LoginPage from "@/pages/LoginPage";
import OverviewPage from "@/pages/OverviewPage";
import PlantsPage from "@/pages/PlantsPage";
import PlantDetailPage from "@/pages/PlantDetailPage";
import EventsPage from "@/pages/EventsPage";
import ReportsPage from "@/pages/ReportsPage";
import ServicePage from "@/pages/ServicePage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import AiWorkspacePage from "@/pages/AiWorkspacePage";
import NotFoundPage from "@/pages/NotFoundPage";
import TilbudProposalPage from "@/pages/TilbudProposalPage";

const FullScreenLoader = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background" data-testid="app-loading">
    <div className="relative">
      <span className="absolute inset-0 rounded-full bg-brand-cyan/40 animate-pulse-ring" aria-hidden="true" />
      <LensMark className="relative h-12 w-12" />
    </div>
    <div className="font-brand text-[11px] font-medium uppercase tracking-[0.28em] text-muted-foreground">Proxima HydroHub</div>
  </div>
);

const RequireAuth = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Outlet />;
};

const RequireProxima = () => {
  const { isProxima } = useAuth();
  return isProxima ? <Outlet /> : <Navigate to="/" replace />;
};

/* embed=1: the proposal page (/tilbud) shows the portal inside a frame and always starts on sign-in */
const PublicOnly = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  if (loading) return <FullScreenLoader />;
  return user && !embed ? <Navigate to="/" replace /> : <Outlet />;
};

function App() {
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={200}>
        <AuthProvider>
          <AssistantProvider>
            <BrowserRouter>
              <div className="App">
                <Routes>
                  <Route path="/tilbud" element={<TilbudProposalPage />} />
                  <Route element={<PublicOnly />}>
                    <Route path="/login" element={<LoginPage />} />
                  </Route>
                  <Route element={<RequireAuth />}>
                    <Route element={<AppShell />}>
                      <Route index element={<OverviewPage />} />
                      <Route path="/kraftverk" element={<PlantsPage />} />
                      <Route path="/kraftverk/:id" element={<PlantDetailPage />} />
                      <Route path="/ai" element={<AiWorkspacePage />} />
                      <Route path="/hendelser" element={<EventsPage />} />
                      <Route path="/rapporter" element={<ReportsPage />} />
                      <Route path="/service" element={<ServicePage />} />
                      <Route path="/innstillinger" element={<SettingsPage />} />
                      <Route element={<RequireProxima />}>
                        <Route path="/admin" element={<AdminPage />} />
                      </Route>
                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  </Route>
                </Routes>
              </div>
              <Toaster position="top-right" richColors={false} closeButton visibleToasts={3} />
            </BrowserRouter>
          </AssistantProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
